import type { Espece, TypeObservation, TypeSignalement } from './types'

/*
  Toute la copie de l'application, en un seul endroit.

  Ton direct, phrases courtes, vocabulaire de l'association : ponte, trace,
  émergence, patrouille, secteur, divagation, désorientation.
  Un bouton dit ce qui se passe quand on l'utilise. Les messages ne s'excusent
  jamais et ne dramatisent pas l'absence de réseau.
*/

export const ESPECE_LIBELLE: Record<Espece, string> = {
  luth: 'Luth',
  olivatre: 'Olivâtre',
  verte: 'Verte',
}

export const ESPECE_LATIN: Record<Espece, string> = {
  luth: 'Dermochelys coriacea',
  olivatre: 'Lepidochelys olivacea',
  verte: 'Chelonia mydas',
}

/** Libellé du toast de confirmation, accordé correctement. */
export const ESPECE_TOAST: Record<Espece, string> = {
  luth: 'Ponte de luth +1',
  olivatre: "Ponte d'olivâtre +1",
  verte: 'Ponte de verte +1',
}

export const OBSERVATION_LIBELLE: Record<TypeObservation, string> = {
  personne_sensibilisee: 'Personnes sensibilisées',
  ponte: 'Ponte observée',
  trace_sans_ponte: 'Trace sans ponte',
  emergence: 'Émergence observée',
}

export const OBSERVATION_TOAST: Record<TypeObservation, string> = {
  personne_sensibilisee: 'Personne sensibilisée +1',
  ponte: 'Ponte +1',
  trace_sans_ponte: 'Trace sans ponte +1',
  emergence: 'Émergence observée +1',
}

export const SIGNALEMENT_LIBELLE: Record<TypeSignalement, string> = {
  eclairage: 'Éclairage gênant',
  chien_divagation: 'Chien en divagation',
  emergence_desorientee: 'Émergence désorientée',
  tortue_blessee: 'Tortue blessée',
}

export const SIGNALEMENT_AIDE: Record<TypeSignalement, string> = {
  eclairage: 'Lampadaire, enseigne, terrasse éclairée vers la plage',
  chien_divagation: 'Chien sans maître sur la zone de ponte',
  emergence_desorientee: 'Nouveau-nés partis vers les terres',
  tortue_blessee: 'Blessure, filet, engin de pêche',
}

/**
 * Mise en garde de l'écran Lampe.
 *
 * Reproduite mot pour mot, telle que fournie. Ne pas reformuler, ne pas
 * abréger, ne pas rendre masquable.
 */
export const AVERTISSEMENT_LAMPE =
  "Un écran rouge réduit fortement la gêne, il ne l'annule pas. La bonne consigne reste le téléphone rangé et l'écran éteint."

export const T = {
  // Onglets
  ongletPatrouille: 'Patrouille',
  ongletSignaler: 'Signaler',
  ongletLampe: 'Lampe',
  ongletSaison: 'Saison',
  ongletAnimer: 'Animer',

  // Barre d'état
  horsReseau: 'HORS RÉSEAU',
  enReseau: 'EN RÉSEAU',
  nuitRouge: 'NUIT ROUGE',

  // Bandeau de file
  gardeSurAppareil: "GARDÉ SUR L'APPAREIL",
  toutEnvoye: 'TOUT ENVOYÉ',
  enFile: 'EN FILE',
  enAttenteDeReprise: 'À REPRENDRE',

  // Patrouille
  patrouilleEnCours: 'Patrouille en cours',
  secteur: 'SECTEUR',
  binome: 'BINÔME',
  demarrerPatrouille: 'Démarrer une patrouille',
  cloturerPatrouille: 'Clôturer la patrouille',
  aucunePatrouille: 'Aucune patrouille en cours',
  aucunePatrouilleAide:
    'Démarrez une patrouille pour compter les observations. Tout reste sur le téléphone.',
  choisirSecteur: 'Secteur de la patrouille',
  choisirSecteurAide: 'Une touche. Le nom du secteur suffit.',
  binomeQuestion: 'Prénoms du binôme',
  binomeAide: 'Facultatif. Prénoms seulement.',
  commencer: 'Commencer',
  choisirEspece: "CHOISIR L'ESPÈCE",
  especeDeLaPonte: 'Espèce de la ponte',
  especeAide: "Une touche. Rien d'autre à remplir.",
  desorientationSignaler: 'DÉSORIENTATION = SIGNALER',
  confirmerCloture: 'Clôturer la patrouille ?',
  confirmerClotureAide: 'Les saisies sont conservées et partiront au retour du réseau.',
  clotureFaite: 'Patrouille clôturée · en file',
  annuler: 'Annuler',
  enregistre: 'ENREGISTRÉ',

  // Signaler
  signalerTitre: 'Signaler',
  signalerAide: 'Quatre situations. Trois touches au maximum.',
  combien: 'Combien ?',
  note: 'Précision',
  noteAide: 'Facultatif.',
  photo: 'Photo',
  photoAide: 'Facultative. Retaillée et vidée de ses métadonnées avant envoi.',
  prendrePhoto: 'Ajouter une photo',
  retirerPhoto: 'Retirer la photo',
  envoyerSignalement: 'Enregistrer le signalement',
  signalementFait: 'Signalement enregistré',

  // Lampe
  lampeTitre: 'Lampe rouge',
  intensite: 'Intensité',
  quitterLampe: 'Quitter la lampe',
  ecranMaintenuAllume: 'ÉCRAN MAINTENU ALLUMÉ',
  ecranNonMaintenu: 'MISE EN VEILLE NON BLOQUÉE',

  // Saison
  saisonTitre: 'Saison',
  pontesParEspece: 'Pontes par espèce',
  autresObservations: 'Autres observations',
  signalementsTitre: 'Signalements',
  derniereSynchro: 'Dernière synchronisation',
  jamaisSynchronise: 'Jamais synchronisé',
  totauxLocaux: 'Chiffres de cet appareil, en attente de synchronisation',

  // Export local
  exporterTitre: 'Exporter',
  exporterAide:
    'Les saisies de ce téléphone, en fichier tableur. Fonctionne sans réseau. Pour le bilan complet de la saison, l’export se fait depuis le serveur.',
  exporterObservations: 'Observations · CSV',
  exporterSignalements: 'Signalements · CSV',
  exportFait: 'Fichier enregistré',
  exportVide: 'Rien à exporter pour le moment',

  // Animer
  animerTitre: 'Fiches d’animation',
  animerAide: 'Consultables sans réseau.',
  retourFiches: 'Retour aux fiches',

  // Code de saison
  codeTitre: 'Code de saison',
  codeAide: 'Le code partagé par l’association. Une seule fois, sur ce téléphone.',
  codePrenom: 'Votre prénom',
  codePrenomAide: 'Facultatif. Sert seulement à identifier le binôme.',
  codeValider: 'Ouvrir la saison',
  codeInvalide: 'Ce code ne correspond à aucune saison ouverte.',
  codeTropDeTentatives: 'Trop de tentatives. Réessayez dans',
  codeReseau: 'Le serveur est injoignable. Réessayez quand le réseau revient.',

  // Thème
  passerEnNuit: 'Passer en mode nuit',
  passerEnNormal: 'Repasser en mode normal',
} as const
